import {
  AliquotAmount,
  EndOrderDetails,
  Establishment,
  TaxVoucherOptions,
} from '@pedix-workspace/utils';
import { ArcaIntegrationDetails, BillingInformationArca, TaxVoucherArca } from './arca.types';
import {
  ARCA_CONCEPT_TYPE_PRODUCTO,
  ARCA_CURRENCY_TYPE_PES,
  ARCA_DOCUMENT_TYPE_OTRO,
  ArcaCurrencyType,
} from './arca.runtime-types';
import {
  generateVoucherRef,
  getArcaAliquotValue,
  getVoucherLetter,
  getVoucherType,
} from './arca.utils';
import { omit } from 'lodash-es';
import { calculateVoucherTotals } from './arca-voucher.utils';

export type GenerateTaxVoucherArcaParams = {
  id: string;
  operation: 'bill' | 'credit'; // TODO: FIX TaxVoucherOperation to remove pending and use it
  establishment: Pick<Establishment, 'id' | 'logo' | 'name'>;
  accountDetails: ArcaIntegrationDetails;
  orderDetails: EndOrderDetails[];
  taxVoucherOptions: TaxVoucherOptions;
  voucherNumber: number;
  cae: string;
  caeFchVto: string;
  observations?: string;
  lineItemsGenerator: (order: EndOrderDetails, params: { defaultAliquot?: AliquotAmount }) => any[];
};
export function generateTaxVoucherArca({
  id,
  operation,
  establishment,
  accountDetails,
  orderDetails,
  taxVoucherOptions,
  voucherNumber,
  cae,
  caeFchVto,
  observations,
  lineItemsGenerator,
}: GenerateTaxVoucherArcaParams): TaxVoucherArca {
  const conceptType = ARCA_CONCEPT_TYPE_PRODUCTO;
  const currencyId = ARCA_CURRENCY_TYPE_PES as ArcaCurrencyType;
  const buyerBillingInfo = <BillingInformationArca>orderDetails[0].billingInformation;
  const voucherType = getVoucherType(
    accountDetails.vatCondition,
    buyerBillingInfo.vatCondition,
    operation,
  );
  const pointOfSale = accountDetails.pointOfSale;
  const defaultAliquot = getArcaAliquotValue(accountDetails.defaultAliquotType);
  const lineItemsByOrder = orderDetails.map(order => ({
    orderId: order.id,
    lineItems: lineItemsGenerator(order, { defaultAliquot }),
  }));
  const voucherLetter = getVoucherLetter(voucherType);
  const voucherTotals = calculateVoucherTotals(lineItemsByOrder, voucherLetter);
  const voucherRef = generateVoucherRef(pointOfSale, voucherNumber);
  const withDocumentData = buyerBillingInfo.documentType && buyerBillingInfo.documentNumber;
  const customerDocumentType = withDocumentData
    ? buyerBillingInfo.documentType
    : ARCA_DOCUMENT_TYPE_OTRO;
  const customerDocumentNumber = withDocumentData ? buyerBillingInfo.documentNumber : undefined;

  return {
    id,
    establishmentId: establishment.id,
    provider: 'arca',
    details: {
      type: voucherType,
      operation,
      created: new Date(),
      updated: new Date(),
      voucherRef,
      lineItemsByOrder,
      buyerBillingInfo,
      sellerInfoExtra: {
        logoUrl: establishment.logo,
        fantasyName: establishment.name,
      },
      defaultAliquot,
      taxVoucherOptions,
      voucherTotals,
      currency: orderDetails[0].currencyCode,
      exchangeRate: 1,
      observations,
      orderIds: orderDetails.map(order => order.id),
      orderSequenceIds: orderDetails.map(order => order.sequenceId),
      cae,
      caeFchVto,
      accountDetails: omit(accountDetails, ['certificate', 'certificateCsr', 'certificateKey']),
      pointOfSale,
      voucherNumber,
      conceptType,
      customerDocumentType,
      customerDocumentNumber,
      currencyId,
    },
  };
}
